import { GetShippingDocumentQuery, WasteKind } from "generated-graphql/graphql";

import { DECLARED_WASTE_KIND_LABELS } from "../ShippingDocuments/extractionReview";
import { formatShippingDocumentDate } from "../ShippingDocuments/shippingDocumentDisplay";
import { SummaryItem, TimelineEvent } from "./sections/summaryStatus";

/**
 * Caption for a step that has not happened. Matches `buildTimelineEvents`, and
 * deliberately not `NO_VALUE_PLACEHOLDER`, which is the empty string: a blank
 * caption renders as a gap under the dot rather than as "nothing yet", and two
 * blank captions collide on `captionLines`'s key.
 */
const PENDING_CAPTION = "\u2014";

type Document = NonNullable<GetShippingDocumentQuery["wasteShippingDocument"]>;
type Line = Document["lines"][number];

/** "1 line" / "3 lines" — the Summary messages count lines constantly. */
function lineCount(count: number): string {
  return count === 1 ? "1 line" : `${count} lines`;
}

/**
 * How many lines the extractor's reading disagrees with. `declaredWasteKind` is
 * what the paper (or a reviewer) says and is nullable; `suggestedWasteKind` is
 * derived from the description and federal codes and is always present. A line
 * with no declared kind is unlabeled, not in disagreement, so it is skipped.
 */
function disagreeingKindCount(lines: readonly Line[]): number {
  return lines.filter(
    (line) => line.declaredWasteKind != null && line.declaredWasteKind !== line.suggestedWasteKind
  ).length;
}

/** The single kind the disagreeing lines were suggested as, or null if mixed. */
function soleSuggestedKind(lines: readonly Line[]): WasteKind | null {
  const kinds = new Set(
    lines
      .filter((line) => line.declaredWasteKind != null)
      .filter((line) => line.declaredWasteKind !== line.suggestedWasteKind)
      .map((line) => line.suggestedWasteKind)
  );
  const [only] = [...kinds];
  return kinds.size === 1 ? only : null;
}

function wasteStreamItem(lines: readonly Line[]): SummaryItem {
  const matched = lines.filter((line) => line.wasteStreamId != null).length;
  if (matched === lines.length) {
    return {
      key: "streams",
      level: "ok",
      message: `All ${lineCount(lines.length)} matched to a waste stream`,
    };
  }
  return {
    key: "streams",
    level: "warning",
    message: `${matched} of ${lineCount(lines.length)} matched to a waste stream`,
    tooltip:
      "A line with no waste stream cannot be matched to a shipment line when this document is attached, because the stream is the key the match is made on. Edit the document to set it.",
  };
}

/**
 * How much of the paper reached the shipment. Only meaningful once a shipment is
 * attached: before that every line is unmatched by definition, and the Timeline
 * already says so. A null `wasteShipmentLineId` on an attached document means the
 * line's stream matched no shipment line, or matched more than one.
 */
function shipmentLineItem(lines: readonly Line[]): SummaryItem {
  const matched = lines.filter((line) => line.wasteShipmentLineId != null).length;
  if (matched === lines.length) {
    return {
      key: "shipmentLines",
      level: "ok",
      message: `All ${lineCount(lines.length)} matched a shipment line`,
    };
  }
  return {
    key: "shipmentLines",
    level: "warning",
    message: `${matched} of ${lineCount(lines.length)} matched a shipment line`,
    tooltip:
      "A line goes unmatched when it names no waste stream, when the container it names is not on this shipment, when its stream appears on no shipment line or on more than one that the waste codes and quantity could not narrow to one, or when two document lines resolve to the same shipment line.",
  };
}

function kindItem(lines: readonly Line[]): SummaryItem {
  const count = disagreeingKindCount(lines);
  const suggested = soleSuggestedKind(lines);
  const reads = count === 1 ? "reads" : "read";
  const message = suggested
    ? `${lineCount(count)} ${reads} as ${DECLARED_WASTE_KIND_LABELS[suggested]}, not the declared kind`
    : `${lineCount(count)} ${reads} as a different waste kind than declared`;
  return {
    key: "declaredKind",
    level: "warning",
    message,
    tooltip:
      "The declared kind is what the document says. The suggested kind is derived from the line's description and federal waste codes. The Lines table shows both.",
  };
}

/**
 * The drawer's at-a-glance read of one shipping document: what the lines are
 * missing, and what disagrees. Every line restates something the Lines table
 * below already shows in detail, the way the manifest drawer's Summary does.
 *
 * Deliberately not here: whether a shipment is attached. That is the Timeline's
 * first real step, and saying it twice in one row of cards reads as two facts.
 */
export function buildShippingDocumentSummaryItems(document: Document): SummaryItem[] {
  const lines = document.lines;

  if (lines.length === 0) {
    return [{ key: "noLines", level: "warning", message: "This document has no lines" }];
  }

  const items: SummaryItem[] = [wasteStreamItem(lines)];

  if (document.linkedShipment) {
    items.push(shipmentLineItem(lines));
  }

  if (disagreeingKindCount(lines) > 0) {
    items.push(kindItem(lines));
  }

  if (!document.shipDate) {
    items.push({
      key: "shipDate",
      level: "warning",
      message: "No ship date on this document",
      tooltip:
        "Nothing supplies a bill of lading's ship date automatically. Without one the 30-day window that normally narrows the shipments offered below does not apply, so they are this facility's unlinked shipments, most recent first.",
    });
  }

  return items;
}

/**
 * The record's lifecycle, left to right: Encamp captured it, a person linked it
 * to a shipment, that shipment shipped.
 *
 * The three steps nest, so the flow never shows a filled dot after a hollow one:
 * a shipped date only exists on a shipment, and a shipment is only here once
 * someone linked it. The waste's own journey is deliberately absent — a bill of
 * lading carries no transit or receipt confirmation the way a manifest does, and
 * its ship date is a field on the Shipping card rather than an event we observed.
 */
export function buildShippingDocumentTimelineEvents(document: Document): TimelineEvent[] {
  const shipment = document.linkedShipment;
  return [
    {
      key: "created",
      label: "Created",
      captionLines: [formatShippingDocumentDate(document.createdAt)],
      done: true,
    },
    {
      key: "linked",
      label: "Linked to shipment",
      captionLines: [shipment?.manifestRequestNumber ?? PENDING_CAPTION],
      done: shipment != null,
    },
    {
      key: "shipped",
      label: "Shipment shipped",
      captionLines: [
        shipment?.shippedDate ? formatShippingDocumentDate(shipment.shippedDate) : PENDING_CAPTION,
      ],
      done: shipment?.shippedDate != null,
    },
  ];
}
